// NeedsYouCard.tsx — (TF-6b-1) the "Needs you" card: the question a run waits on, as words and numbers (lib/needs-input.ts),
// with the choices the question lists. One card for every door: the video page (the owner, through /api/videos/:id/needs-input)
// and the admin's (the Run Inspector and the growth arc's video panel, through /api/admin/runs/:runId/needs-input). It sends
// back the questionId it DISPLAYED; a changed question is read again and said. After an answer it says "Sent" until the run
// moves on.
import { useState } from "react";
import { TfCard, Btn } from "@/components/v2/primitives.tsx";
import { cardFor, sentState, answerError, type NeedsInputView } from "@/lib/needs-input.ts";
import { w, useWords } from "@/lib/words.ts";   // (B107)

export function NeedsYouCard({ ni, admin, retakeCredits, answer, onAnswered, onChanged }: {
  ni: NeedsInputView; admin: boolean; retakeCredits?: number | null;
  answer: (choice: string, questionId: string | null) => Promise<unknown>;
  onAnswered: () => void; onChanged: () => void;
}) {
  useWords();   // (B107) the store's words wherever the card shows (the video page, Admin → Waiting on you)
  const [sent, setSent] = useState<{ questionId: string | null; choice: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<{ changed: boolean; text: string } | null>(null);
  const card = cardFor(ni, admin, retakeCredits);
  const state = sentState(sent, ni);
  const press = async (choice: string) => {
    setBusy(true); setErr(null);
    const shown = ni.questionId ?? null;
    try { await answer(choice, shown); setSent({ questionId: shown, choice }); onAnswered(); }
    catch (e) { const x = answerError(e); setErr(x); if (x.changed) onChanged(); }   // catch-reason: shown on the card (a changed question is read again)
    finally { setBusy(false); }
  };
  return (
    <TfCard>
      <h2 style={{ margin: 0 }}>{card.title}</h2>
      {state === "changed" && <div className="tf-muted" style={{ fontSize: 13, marginTop: 6 }}>{w("page.needs_you.changed")}</div>}
      {card.lines.length > 0 && <ul style={{ margin: "8px 0 12px", paddingLeft: 18 }}>{card.lines.map((l) => <li key={l}>{l}</li>)}</ul>}
      {state === "sent" ? (
        <div style={{ fontSize: 13 }}><strong>{w("page.needs_you.sent")}</strong> <span className="tf-muted">{w("page.needs_you.sent_note")}</span></div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {card.buttons.map((b) => (
            <div key={b.choice}>
              <Btn onClick={() => press(b.choice)} disabled={busy}>{b.label}</Btn>
              {b.note && <div className="tf-muted" style={{ fontSize: 12, marginTop: 4 }}>{b.note}</div>}
            </div>
          ))}
          {card.noAnswer && <a href={card.noAnswer.href}><Btn variant="ghost">{card.noAnswer.label}</Btn></a>}
        </div>
      )}
      {err && <div style={{ color: "var(--tf-danger, #c00)", fontSize: 13, marginTop: 8 }}>{err.text}</div>}
      {ni.expiresAt && <div className="tf-muted" style={{ fontSize: 12, marginTop: 10 }}>{w("page.needs_you.kept_until", { date: new Date(ni.expiresAt).toLocaleDateString() })}</div>}
    </TfCard>
  );
}
